import { ZodError } from 'zod';
import { NavigableError } from './NavigableError';

const FALLBACK = 'An unexpected error occurred. Please try again.';

const STATUS_MESSAGES: Record<number, string> = {
  401: 'Your session has expired. Please sign in again.',
  403: 'You do not have permission to perform this action.',
  404: 'The requested resource could not be found.',
  429: 'Too many requests. Please wait a moment and try again.',
  503: 'The service is temporarily unavailable. Please try again later.',
};

const SERVER_ERROR = 'Something went wrong on our end. Please try again later.';

/** Matches "Request failed (NNN)", "Create run failed (NNN)", "Start run failed (NNN)" */
const STATUS_CODE_RE = /failed \((\d{3})\)/;

const WORKFLOW_FAILED_RE = /^Workflow failed:/;

/** Returns true when the message looks like raw technical output users shouldn't see. */
function isTechnicalMessage(message: string): boolean {
  return /unexpected token/i.test(message) || /^\s*</.test(message);
}

/**
 * Translates a caught error into a user-friendly message for the ErrorBoundary.
 *
 * Priority order:
 * 1. NavigableError — pass through (already user-facing)
 * 2. ZodError — generic "unexpected response" message
 * 3. API / workflow errors — status-code-based or generic server message
 * 4. BFF-provided messages — pass through if not technical
 * 5. Everything else — fallback
 */
export function translateError(error: unknown): string {
  if (error instanceof NavigableError) {
    return error.message;
  }

  if (
    error instanceof ZodError ||
    (error instanceof Error && error.name === 'ZodError')
  ) {
    return 'We received an unexpected response from the server.';
  }

  if (error instanceof Error && error.message) {
    const statusMatch = error.message.match(STATUS_CODE_RE);
    if (statusMatch) {
      const code = Number(statusMatch[1]);
      return STATUS_MESSAGES[code] ?? SERVER_ERROR;
    }

    if (WORKFLOW_FAILED_RE.test(error.message)) {
      return SERVER_ERROR;
    }

    if (!isTechnicalMessage(error.message)) {
      return error.message;
    }
  }

  return FALLBACK;
}
